'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  User,
} from 'firebase/auth'
import { createSession } from '../../actions/auth'
import { app } from '../../lib/firebase'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

export default function LoginForm() {
  const t = useTranslations('Login')
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const handleLogin = async (user: User) => {
    // Get ID token Result to check custom claims
    const idTokenResult = await user.getIdTokenResult()
    const idToken = idTokenResult.token

    if (!idTokenResult.claims.tenantId) {
      // Authenticated but no tenant -> create session to allow access to /onboarding
      const res = await createSession(idToken)
      if (res.success) {
        router.push('/onboarding')
      } else {
        setError(res.message || t('loginFailed'))
      }
      return
    }

    // Create session cookie via server action
    const res = await createSession(idToken)

    if (res.success) {
      router.push('/dashboard')
    } else {
      setError(res.message || t('loginFailed'))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    const auth = getAuth(app)
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      )
      await handleLogin(userCredential.user)
    } catch (err: unknown) {
      console.error('Login error:', err)
      setError(t('invalidAuth'))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError(null)
    const auth = getAuth(app)
    const provider = new GoogleAuthProvider()

    try {
      const result = await signInWithPopup(auth, provider)
      await handleLogin(result.user)
    } catch (err: unknown) {
      console.error('Google Sign In Error:', err)
      setError(t('googleFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-2">
      <h1 className="text-3xl font-bold mb-6">{t('title')}</h1>
      <div className="w-full max-w-xs">
        <form onSubmit={handleSubmit} className="mb-4">
          <input
            type="email"
            placeholder={t('email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-4"
            required
          />
          <input
            type="password"
            placeholder={t('password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-4"
            required
          />
          {error && <p className="text-red-500 text-xs italic mb-4">{error}</p>}
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
            disabled={loading}
          >
            {loading ? t('loggingIn') : t('submit')}
          </button>
        </form>

        <div className="text-center mb-4 text-gray-500">{t('or')}</div>

        <button
          onClick={handleGoogleSignIn}
          type="button"
          disabled={loading}
          className="w-full bg-white border border-gray-300 text-gray-700 font-bold py-2 px-4 rounded hover:bg-gray-50 flex items-center justify-center shadow-sm"
        >
          <Image
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            width={20}
            height={20}
            className="mr-3"
            alt="Google"
          />
          {t('googleSignIn')}
        </button>
      </div>
    </div>
  )
}
